import { Button, Col, Form, Input, message, Row, Switch } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Spinner from "../../../components/spinner";
import { getApiErrorMsg, getApiResponseMsg } from "../../../utils/object-util";
import styles from "./form.module.scss";

const { Item } = Form;

interface IWebhookDetailsProps {
	mode: "create" | "edit" | null;
	id: string | null;
	setActiveKey: (arg: string) => void;
}

const WebhookDetails = ({ mode, id, setActiveKey }: IWebhookDetailsProps) => {
	const isEditing = mode === "edit" && id !== null;
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [loading, setLoading] = useState(isEditing);

	useEffect(() => {
		if (isEditing) {
			setLoading(true);
			axios
				.get(`events/${id}/webhooks`)
				.then((response) => {
					form.setFieldsValue(response.data.data);
				})
				.catch((error) => {
					message.error({ content: getApiErrorMsg(error), key: "error", duration: 2 });
				})
				.finally(() => {
					setLoading(false);
				});
		}
	}, [mode, id]);

	const handleSubmit = (data: any) => {
		setIsSubmitting(true);
		const payload = { ...data, isActive: data.isActive ?? true };
		const trimmedDescription = payload.description?.trim();
		if (trimmedDescription) {
			payload.description = trimmedDescription;
		} else {
			delete payload.description;
		}

		axios
			.put(`events/${id}/webhooks`, payload)
			.then((res) => {
				if (res?.data?.data) {
					message.success({ content: getApiResponseMsg(res), key: "success", duration: 2 });
				}
			})
			.catch((error) => {
				message.error({ content: getApiErrorMsg(error), key: "error", duration: 2 });
			})
			.finally(() => {
				if (mode === "create") {
					navigate(`/events/event-types`);
				} else {
					setActiveKey("0");
				}
				setIsSubmitting(false);
			});
	};

	if (loading) return <Spinner />;

	return (
		<>
			<Row>
				<Col sm={12}>
					<Form onFinish={handleSubmit} form={form} layout="vertical" initialValues={{ isActive: false, eventTypes: [] }} disabled={isSubmitting}>
						<Item
							name="url"
							label="Webhook URL"
							rules={[
								{ required: true, message: "Webhook URL is required" },
								{ type: "url", message: "Please enter a valid URL" },
								{
									validator: (_: any, value: string) => {
										if (value && !value.startsWith("https://")) {
											return Promise.reject("Webhook URL must start with https://");
										}
										return Promise.resolve();
									},
								},
								{ max: 1000, message: "URL must be less than 1000 characters" },
							]}
						>
							<Input placeholder="https://your-webhook-endpoint.com" />
						</Item>

						<Item name="description" label="Description" rules={[{ max: 1000, message: "Description must be less than 1000 characters" }]}>
							<Input.TextArea placeholder="Optional description for this webhook" autoSize={{ minRows: 2, maxRows: 5 }} />
						</Item>
						<Item name="secret" label="Secret" rules={[{ required: true, whitespace: true, message: "Secret is required" }]}>
							<Input.Password placeholder="Enter webhook secret" />
						</Item>

						<Item name="isActive" label="Webhook Active?" valuePropName="checked">
							<Switch />
						</Item>
					</Form>
				</Col>
			</Row>
			<div className={styles["submit-btn-cont"]}>
				<Button
					onClick={() => {
						if (mode === "edit") {
							setActiveKey("0");
						} else {
							navigate("/events/event-types");
						}
					}}
					disabled={isSubmitting}
				>
					Cancel
				</Button>
				<Button onClick={form.submit} type="primary" loading={isSubmitting}>
					{mode === "edit" ? "Save and update" : "Next"}
				</Button>
			</div>
		</>
	);
};

export default WebhookDetails;
